import { useCallback, useEffect, useState } from "react";
import {
  useGetMarketerByMarketerIdQuery,
  useGetSubscriptionMarketingQuery,
} from "../features/marketing/marketingApiSlice";

export const useViewMarketerProfile = (marketerId) => {
  const [profileData, setProfileData] = useState(null);
  const [topReview, setTopReview] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [projectDetail, setProjectDetail] = useState({});

  const {
    data: marketerResponse,
    isLoading: loadingMarketer,
    error: marketerError,
  } = useGetMarketerByMarketerIdQuery(marketerId, {
    skip: !marketerId,
  });

  const { data: subscriptionResponse, isLoading: loadingSubscription } =
    useGetSubscriptionMarketingQuery();

  useEffect(() => {
    if (marketerResponse) {

      const extractedData = marketerResponse?.data?.data || marketerResponse?.data || marketerResponse;

      // Check if response has success flag (common pattern)
      const isSuccessResponse = marketerResponse?.success === true || marketerResponse?.data?.success === true;

      // Try extracting from different levels
      let actualData = extractedData;

      // If the response is wrapped differently, try to unwrap it
      if (marketerResponse?.data?.data && !actualData?.userId) {
        actualData = marketerResponse.data.data;
      } else if (marketerResponse?.data && !actualData?.userId && marketerResponse.data.data) {
        actualData = marketerResponse.data.data;
      }
      // Only set if we have valid data
      if (actualData && (actualData._id || actualData.marketerId)) {
        // Set profile data
        setProfileData(actualData);

        // Extract top review section
        const topReviewData = actualData?.topReviewSection || marketerResponse?.topReviewSection;
        setTopReview(topReviewData || null);

        // Extract ratings array
        const ratingsData = Array.isArray(actualData?.ratings)
          ? actualData.ratings
          : Array.isArray(marketerResponse?.ratings)
            ? marketerResponse.ratings
            : [];
        setReviews(ratingsData);

        // Extract project stats
        const projectStatsData = actualData?.projectStats || marketerResponse?.projectStats || {};
        setProjectDetail(projectStatsData);
      } else {
        console.warn("⚠️ Invalid marketer data structure:", actualData);
      }
    }
  }, [marketerResponse]);

  const timeAgo = useCallback((date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1)
      return (
        Math.floor(interval) + " year" + (interval >= 2 ? "s ago" : " ago")
      );

    interval = seconds / 2592000;
    if (interval > 1)
      return (
        Math.floor(interval) + " month" + (interval >= 2 ? "s ago" : " ago")
      );

    interval = seconds / 86400;
    if (interval > 1)
      return Math.floor(interval) + " day" + (interval >= 2 ? "s ago" : " ago");

    interval = seconds / 3600;
    if (interval > 1)
      return (
        Math.floor(interval) + " hour" + (interval >= 2 ? "s ago" : " ago")
      );

    interval = seconds / 60;
    if (interval > 1)
      return (
        Math.floor(interval) + " minute" + (interval >= 2 ? "s ago" : " ago")
      );

    return Math.floor(seconds) + " sec ago";
  }, []);

  const paymentPlans = (() => {
    const raw =
      subscriptionResponse?.data?.data ||
      subscriptionResponse?.data ||
      subscriptionResponse ||
      [];
    const plans = Array.isArray(raw) ? raw : [];
    const order = ["Basic", "Standard", "Premium"];
    return plans
      .filter((plan) => plan && plan.planName && plan.planName !== "Free")
      .sort(
        (a, b) => order.indexOf(a.planName) - order.indexOf(b.planName),
      );
  })();

  return {
    profileData,
    topReview,
    reviews,
    projectDetail,
    paymentPlans,
    loading: loadingMarketer || loadingSubscription,
    error: marketerError,
    timeAgo,
  };
};

export default useViewMarketerProfile;
